/**
 * FlahaSOIL v2 — Soil-test detail page.
 *
 * Scan path: sticky actions → summary hero → top constraints →
 * warnings → tabs (results / scientific charts).
 */
import {
	Alert,
	Box,
	Button,
	CardContent,
	CardHeader,
	Chip,
	Divider,
	Grid,
	Skeleton,
	Stack,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import type {
	EntitlementSnapshot,
	GetSoilTestResponse,
} from "@flaha/shared-types";
import { SoilReportStatus } from "@flaha/shared-types";

import { ChemistryResultsCard } from "../features/results/components/ChemistryResultsCard";
import { ConstraintRadar } from "../features/results/components/ConstraintRadar";
import { InterpretationCard } from "../features/results/components/InterpretationCard";
import { PhysicsResultsCard } from "../features/results/components/PhysicsResultsCard";
import { freeResultsSurface } from "../features/plan/planSurface";
import { ScientificChartsPanel } from "../features/soil/charts";
import { SummaryCard } from "../features/results/components/SummaryCard";
import { TopConstraintsPanel } from "../features/results/components/TopConstraintsPanel";
import { WarningList } from "../features/results/components/WarningList";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";
import { useAuth } from "../auth";
type SoilTestTab = "results" | "scientific";

export function SoilTestDetailPage() {
	const { soilTestId = "" } = useParams<{ soilTestId: string }>();
	const navigate = useNavigate();
	const { activeOrganization } = useAuth();
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
	const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
	const [data, setData] = useState<GetSoilTestResponse | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [generating, setGenerating] = useState(false);
	const [tab, setTab] = useState<SoilTestTab>("results");
	const [entitlements, setEntitlements] = useState<EntitlementSnapshot | null>(
		null
	);
	const requestId = useRef(0);
	const surface = freeResultsSurface(entitlements);

	const onGenerateReport = async () => {
		if (generating) return;
		setGenerating(true);
		setActionError(null);
		try {
			const { report } = await getApiClient().generateReport(soilTestId, {});
			navigate(`/reports/${report.id}`);
		} catch (err: unknown) {
			setActionError(err instanceof Error ? err.message : String(err));
		} finally {
			setGenerating(false);
		}
	};

	usePageHeader({
		title: "Soil test results",
		subtitle: data
			? `Test ${data.soilTest.id} · sample ${data.soilTest.sampleId}`
			: "Loading test…",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Projects", to: "/projects" },
			{ label: "Soil test" },
		],
	});

	useEffect(() => {
		let cancelled = false;
		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (!cancelled) setEntitlements(res.usage.entitlements);
			})
			.catch(() => {
				if (!cancelled) setEntitlements(null);
			});
		return () => {
			cancelled = true;
		};
	}, [activeOrganization?.id]);

	const load = useCallback(() => {
		const currentRequest = ++requestId.current;
		setLoadError(null);
		setData(null);
		void getApiClient()
			.getSoilTest(soilTestId)
			.then((d) => {
				if (requestId.current === currentRequest) setData(d);
			})
			.catch((err: unknown) => {
				if (requestId.current === currentRequest) {
					setLoadError(err instanceof Error ? err.message : String(err));
				}
			});
	}, [soilTestId]);

	useEffect(() => {
		setActionError(null);
		load();
	}, [load, activeOrganization?.id]);

	if (loadError) {
		return (
			<Alert
				severity="error"
				action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
			>
				Failed to load soil test {soilTestId}: {loadError}
			</Alert>
		);
	}

	if (!data) {
		return (
			<GlassSurface aria-label="Loading soil-test results" aria-busy="true" sx={{ p: 3 }}>
				<Skeleton width="52%" height={42} />
				<Skeleton width="70%" />
				<Skeleton height={180} />
			</GlassSurface>
		);
	}

	const warnings = data.interpretation?.warningsJson ?? [];

	return (
		<Box
			data-testid="soil-test-results-workspace"
			data-layout={isCompact ? "compact" : "wide"}
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={{ minWidth: 0 }}
		>
			{actionError ? (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
					Report generation failed: {actionError}
				</Alert>
			) : null}

			{/* Sticky decision bar — denser on phone so content remains visible */}
			<GlassSurface
				component="div"
				intensity="strong"
				depth="raised"
				sx={{
					position: "sticky",
					top: { xs: 56, sm: 64 },
					zIndex: theme.zIndex.appBar - 2,
					mb: { xs: 2, sm: 3 },
					px: { xs: 1.25, sm: 2 },
					py: { xs: 1, sm: 1.25 },
					backdropFilter: "blur(16px)",
				}}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					justifyContent="space-between"
					alignItems={{ xs: "stretch", sm: "center" }}
					spacing={{ xs: 1, sm: 1.25 }}
				>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
						{surface.showAgronomicInterpretation && data.interpretation ? (
							<Chip
								label={`Overall: ${data.interpretation.overallSoilRating}`}
								color={
									data.interpretation.overallSoilRating === "GOOD"
										? "success"
										: data.interpretation.overallSoilRating === "FAIR"
											? "warning"
											: data.interpretation.overallSoilRating === "POOR"
												? "error"
												: "default"
								}
								size="small"
								sx={{ fontWeight: 700 }}
							/>
						) : null}
						{data.physicsResult?.textureClass ? (
							<Chip
								label={data.physicsResult.textureClass}
								size="small"
								variant="outlined"
							/>
						) : null}
						<Chip
							label={data.soilTest.testLevel}
							size="small"
							variant="outlined"
						/>
					</Stack>
					{surface.showReportActions ? (
						<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
							<Button
								component={RouterLink}
								to={`/soil-tests/${data.soilTest.id}/report`}
								variant="outlined"
							>
								Open on-screen preview
							</Button>
							<Button
								variant="contained"
								onClick={onGenerateReport}
								disabled={generating}
							>
								{generating ? "Creating report…" : "Create versioned report"}
							</Button>
						</Stack>
					) : (
						<Button
							component={RouterLink}
							to="/pricing"
							variant="outlined"
							size="small"
						>
							Upgrade for formal reports
						</Button>
					)}
				</Stack>
			</GlassSurface>

			<SummaryCard
				soilTest={data.soilTest}
				physics={data.physicsResult}
				interpretation={
					surface.showAgronomicInterpretation ? data.interpretation : null
				}
			/>

			{surface.showConstraintPanels || surface.showReportActions ? (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						md: "minmax(0, 1fr) minmax(0, 1fr)",
						lg: "minmax(280px, 0.9fr) minmax(0, 1.1fr) minmax(240px, 0.85fr)",
					},
					gap: { xs: 1.5, sm: 2 },
					mb: { xs: 2, sm: 3 },
					alignItems: "stretch",
				}}
			>
				{surface.showConstraintPanels ? (
					<>
						<ConstraintRadar interpretation={data.interpretation} />
						<TopConstraintsPanel interpretation={data.interpretation} />
					</>
				) : null}
				{surface.showReportActions ? (
				<GlassSurface
					component="section"
					aria-labelledby="next-actions-title"
					intensity="subtle"
					depth="raised"
					sx={{ p: { xs: 2, md: 2.5 } }}
				>
					<Typography
						id="next-actions-title"
						component="h2"
						variant="overline"
						sx={{ color: "workspace.blue", letterSpacing: "0.1em", fontWeight: 700 }}
					>
						Next actions
					</Typography>
					<Typography component="p" variant="h6" sx={{ mt: 0.25, mb: 1.5 }}>
						Preserve evidence and share findings
					</Typography>
					<Stack spacing={1}>
						<Button
							variant="contained"
							onClick={onGenerateReport}
							disabled={generating}
							fullWidth
						>
							{generating ? "Creating report…" : "Create versioned report"}
						</Button>
						<Button
							component={RouterLink}
							to={`/soil-tests/${data.soilTest.id}/report`}
							variant="outlined"
							fullWidth
						>
							Open on-screen preview
						</Button>
						<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
							<strong>Versioned report</strong> freezes the snapshot for audit and
							PDF download. <strong>On-screen preview</strong> is a quick view
							without creating a new version.
						</Typography>
					</Stack>
				</GlassSurface>
				) : null}
			</Box>
			) : null}

			{warnings.length > 0 ? (
				<Box component="section" aria-labelledby="warnings-title" sx={{ mb: 3 }}>
					<Typography id="warnings-title" component="h2" variant="h6" sx={{ mb: 1 }}>
						Warnings and limitations
					</Typography>
					<WarningList warnings={warnings} />
				</Box>
			) : null}

			<GlassSurface intensity="subtle" depth="flat" sx={{ mb: 2, px: 1 }}>
				<Tabs
					value={tab}
					onChange={(_, v: SoilTestTab) => setTab(v)}
					aria-label="Soil test sections"
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab value="results" label="Results" />
					<Tab value="scientific" label="Scientific visuals" />
				</Tabs>
			</GlassSurface>

			{tab === "results" ? (
				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>
						<PhysicsResultsCard result={data.physicsResult} />
					</Grid>
					{surface.showChemistryDetail ? (
					<Grid item xs={12} md={6}>
						<ChemistryResultsCard
							result={data.chemistryResult}
							testLevel={data.soilTest.testLevel}
							chemistryInput={data.chemistryInput}
						/>
					</Grid>
					) : null}
					{surface.showAgronomicInterpretation ? (
					<Grid item xs={12} md={6}>
						<InterpretationCard interpretation={data.interpretation} />
					</Grid>
					) : (
					<Grid item xs={12} md={6}>
						<GlassSurface sx={{ p: 2.5, height: "100%" }}>
							<Typography variant="h6" gutterBottom>
								Agronomic interpretation
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Full soil analysis and agronomic interpretation notes are
								included on Professional and Enterprise plans.
							</Typography>
							<Button component={RouterLink} to="/pricing" variant="outlined" size="small">
								View plans
							</Button>
						</GlassSurface>
					</Grid>
					)}
					{surface.showReportActions ? (
					<Grid item xs={12} md={6}>
						<GlassSurface
							component="section"
							aria-labelledby="reports-results-title"
							sx={{ height: "100%" }}
						>
							<CardHeader
								title="Reports"
								titleTypographyProps={{
									id: "reports-results-title",
									component: "h2",
									variant: "h6",
								}}
								action={
									<Chip
										label="Traceable output"
										size="small"
										variant="outlined"
									/>
								}
							/>
							<Divider sx={{ borderColor: "workspace.border" }} />
							<CardContent>
								{data.reports.length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No reports generated yet. Use &quot;Create versioned
										report&quot; above to create the first version.
									</Typography>
								) : (
									<Stack spacing={1}>
										{data.reports.map((r) => (
											<Button
												key={r.id}
												component={RouterLink}
												to={`/reports/${r.id}`}
												variant="text"
												sx={{ justifyContent: "flex-start" }}
											>
												{r.title ?? r.reportNumber ?? "Report"} — v
												{r.latestVersionNumber} ·{" "}
												{r.status === SoilReportStatus.READY
													? "Ready"
													: r.status}
											</Button>
										))}
									</Stack>
								)}
							</CardContent>
						</GlassSurface>
					</Grid>
					) : null}
				</Grid>
			) : (
				<ScientificChartsPanel
					soilTestId={data.soilTest.id}
					initialFullTest={data}
					textureTriangleOnly={surface.showTextureTriangleOnly}
				/>
			)}
		</Box>
	);
}
